import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useInvites } from '../context/InvitesContext';
import { AuthCard } from './LoginProcess/AuthCard';

export const InvitePage = (): React.ReactNode => {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';
  const navigate = useNavigate();
  const { firebaseUser, loading: authLoading } = useAuth();
  const { acceptInviteWithRetry } = useInvites();
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    message: string;
    tripId?: string;
  } | null>(null);

  useEffect(() => {
    const handleInvite = async () => {
      if (!code) {
        setResult({
          success: false,
          message: 'Invalid invite link',
        });
        return;
      }

      // If demo mode, always show auth form regardless of login state
      if (isDemo) {
        return;
      }

      // If user is not logged in, show auth form (don't redirect)
      if (!firebaseUser) {
        return;
      }

      // User is logged in, process the invite
      setProcessing(true);
      try {
        const acceptResult = await acceptInviteWithRetry(code, firebaseUser.uid);

        if (acceptResult.success) {
          setResult({
            success: true,
            message: 'Successfully joined the trip!',
            tripId: acceptResult.trip_id,
          });
          // Redirect to trip after 2 seconds
          setTimeout(() => {
            navigate(`/trip/${acceptResult.trip_id}`);
          }, 2000);
        } else {
          setResult({
            success: false,
            message: acceptResult.error || 'Failed to accept invite',
          });
        }
      } catch (error) {
        console.error('Error accepting invite:', error);
        setResult({
          success: false,
          message: 'An error occurred while accepting the invite',
        });
      } finally {
        setProcessing(false);
      }
    };

    handleInvite();
  }, [code, firebaseUser, navigate, isDemo, acceptInviteWithRetry]);

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <CardTitle>Loading...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Show auth form if user is not logged in OR if in demo mode
  if ((!firebaseUser || isDemo) && !processing && !result) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        {isDemo && (
          <div className="mb-4 rounded-lg bg-blue-50 border border-blue-200 px-4 py-2">
            <p className="text-sm text-blue-800">
              🎭 <strong>Demo Preview:</strong> This is how the invite page looks to recipients
            </p>
          </div>
        )}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold tracking-tight mb-2">
            You've Been Invited to Join a Trip!
          </h1>
          <p className="text-lg text-muted-foreground">
            Create an account or sign in to accept the invitation
          </p>
        </div>
        <AuthCard
          title="Get Started"
          description="Create your account to join the trip"
          buttonText="Create Account & Join"
          isSignup={true}
          toggleText="Already have an account?"
          toggleLink={`/login?invite=${code}`}
        />
      </div>
    );
  }

  // Show processing state
  if (processing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <CardTitle>Processing Invite</CardTitle>
            <CardDescription>Adding you to the trip...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${
            result?.success ? 'bg-green-100' : 'bg-red-100'
          }`}>
            {result?.success ? (
              <CheckCircle className="h-8 w-8 text-green-600" />
            ) : (
              <XCircle className="h-8 w-8 text-red-600" />
            )}
          </div>
          <CardTitle>{result?.success ? (isDemo ? 'Demo Preview' : 'Success!') : 'Invite Error'}</CardTitle>
          <CardDescription>{result?.message}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {isDemo ? (
            <Button onClick={() => navigate(-1)} variant="outline">
              Back to Settings
            </Button>
          ) : result?.success && result?.tripId ? (
            <Button onClick={() => navigate(`/trip/${result.tripId}`)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Go to Trip
            </Button>
          ) : (
            <Button onClick={() => navigate('/trips')} variant="outline">
              Go to Trips
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
